import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

class ProductInventory extends Component {
  /** @type {Map<string, { id: number; quantity: number; barPercent?: number; managed: boolean; oos: boolean; status: string; cardMessage: string; statusMessage: string }> | null} */
  #inventoryByVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    this.#reloadInventoryVariantMap();
    this.#applyFromVariantMap();
    this.#applyInventoryBarWidth();
    requestAnimationFrame(() => {
      this.#applyFromVariantMap();
      this.#applyInventoryBarWidth();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Matches Liquid `blocks/product-inventory.liquid` (integer division, then clamp 4–100).
   * @param {{ quantity: unknown; managed?: boolean; oos?: boolean; status?: string }} entry
   */
  #computeBarWidthPercentFromEntry(entry) {
    const barCap = Math.max(1, Number.parseInt(this.dataset.barScaleMax || '100', 10));
    const showQty = this.dataset.showInventoryQuantity === 'true';
    const qty = Math.max(0, Math.floor(Number(entry.quantity) || 0));
    const managed = entry.managed === true;
    const status = typeof entry.status === 'string' ? entry.status : '';
    const oos = entry.oos === true || status === 'out_of_stock';

    let raw = 0;
    if (managed && qty > 0) {
      if (status === 'low') {
        raw = showQty ? Math.floor((qty * 100) / barCap) : 35;
      } else {
        raw = Math.floor((qty * 100) / barCap);
      }
    } else if (oos) {
      raw = 4;
    } else {
      raw = 38;
    }

    if (raw > 100) raw = 100;
    if (raw < 4) raw = 4;
    return raw;
  }

  /**
   * Sets bar width: when per-variant JSON exists, derives width from `quantity` + block settings (same as Liquid). Otherwise random or data-server-bar-percent.
   */
  #applyInventoryBarWidth = () => {
    const fill = this.querySelector('.js-inventory-bar-fill');
    if (!(fill instanceof HTMLElement)) return;

    const entry = this.#getCurrentVariantInventoryEntry();
    if (this.#inventoryByVariantId instanceof Map && this.#inventoryByVariantId.size > 0 && entry) {
      const pct = this.#computeBarWidthPercentFromEntry(entry);
      fill.style.width = `${pct}%`;
      this.dataset.serverBarPercent = String(pct);
      return;
    }

    const randomize = this.dataset.randomizeProgressBar === 'true';
    if (randomize) {
      const min = Number.parseFloat(this.dataset.randomBarMin || '18');
      const max = Number.parseFloat(this.dataset.randomBarMax || '72');
      const lo = Math.min(min, max);
      const hi = Math.max(min, max);
      const pct = lo + Math.random() * (hi - lo);
      fill.style.width = `${pct.toFixed(1)}%`;
    } else {
      const server = this.dataset.serverBarPercent;
      if (server != null && server !== '') {
        fill.style.width = `${server}%`;
      }
    }
  };

  #reloadInventoryVariantMap() {
    this.#inventoryByVariantId = null;
    const el = this.querySelector('script[type="application/json"][data-inventory-by-variant]');
    if (!(el instanceof HTMLScriptElement) || !el.textContent?.trim()) return;
    try {
      const rows = JSON.parse(el.textContent);
      if (!Array.isArray(rows) || rows.length === 0) return;
      this.#inventoryByVariantId = new Map(rows.map((row) => [String(row.id), row]));
    } catch {
      this.#inventoryByVariantId = null;
    }
  }

  /** @returns {{ id: number; quantity: number; barPercent?: number; managed: boolean; oos: boolean; status: string; cardMessage: string; statusMessage: string } | undefined} */
  #getCurrentVariantInventoryEntry() {
    const map = this.#inventoryByVariantId;
    const vid = this.dataset.currentVariantId;
    if (!(map instanceof Map) || map.size === 0 || vid == null || vid === '') return undefined;
    return map.get(String(vid));
  }

  /** Applies translated copy, OOS class, and quantity badge from per-variant JSON; bar width uses the same quantity via #applyInventoryBarWidth. */
  #applyFromVariantMap() {
    const entry = this.#getCurrentVariantInventoryEntry();
    if (!entry) return;

    const card = this.querySelector('.product-inventory-card__message');
    if (card instanceof HTMLElement && entry.cardMessage != null && entry.cardMessage !== '') {
      card.textContent = entry.cardMessage;
    }

    const line = this.querySelector('.product-inventory__text');
    if (line instanceof HTMLElement && entry.statusMessage) {
      line.textContent = entry.statusMessage;
    }

    if (entry.oos) {
      this.classList.add('product-inventory--oos');
    } else {
      this.classList.remove('product-inventory--oos');
    }

    const badge = this.querySelector('.js-inventory-qty-badge');
    if (badge instanceof HTMLElement) {
      const showQty = this.dataset.showInventoryQuantity === 'true';
      const qty = Math.max(0, Math.floor(Number(entry.quantity) || 0));
      if (entry.managed === true && showQty && qty > 0) {
        badge.textContent = String(qty);
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    }
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const doc = event.detail.data.html;
    const pid = this.dataset.productId;
    const newInventory =
      (pid && doc.querySelector(`product-inventory[data-product-id="${pid}"]`)) ||
      doc.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
    this.#mergeRootFromFetched(newInventory);
    const resourceId = event.detail?.resource?.id;
    if (resourceId != null && resourceId !== '') {
      this.dataset.currentVariantId = String(resourceId);
    }
    this.#reloadInventoryVariantMap();
    this.#syncInventoryCopyFrom(newInventory);
    this.#applyFromVariantMap();
    this.#applyInventoryBarWidth();
    requestAnimationFrame(() => {
      this.#applyFromVariantMap();
      this.#applyInventoryBarWidth();
    });
  };

  /**
   * Copy server-rendered status text after morph (avoids stale counts when the bar re-runs).
   * @param {Element} source
   */
  #syncInventoryCopyFrom(source) {
    const fromCard = source.querySelector('.product-inventory-card__message');
    const toCard = this.querySelector('.product-inventory-card__message');
    if (fromCard instanceof HTMLElement && toCard instanceof HTMLElement) {
      toCard.textContent = fromCard.textContent;
    }

    const fromLine = source.querySelector('.product-inventory__text');
    const toLine = this.querySelector('.product-inventory__text');
    if (fromLine instanceof HTMLElement && toLine instanceof HTMLElement) {
      toLine.textContent = fromLine.textContent;
    }
  }

  /**
   * childrenOnly morph does not refresh the host attributes; copy class, style, and data-* from fetched markup.
   * @param {Element} source
   */
  #mergeRootFromFetched(source) {
    if (!(source instanceof HTMLElement)) return;
    const cls = source.getAttribute('class');
    if (cls) this.setAttribute('class', cls);
    const st = source.getAttribute('style');
    if (st) this.setAttribute('style', st);
    for (const attr of source.attributes) {
      if (attr.name.startsWith('data-')) {
        this.setAttribute(attr.name, attr.value);
      }
    }
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
